/**
 * LoanContext - Global loan state management
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import {
  Loan,
  EmiDue,
  Fee,
  PrecloseLoanDetails,
  createDefaultEmiDue,
  createDefaultFee,
  createDefaultPrecloseLoanDetails,
} from "../types/loan.types";
import { NachRepayment } from "../types/nach.types";
import utilsService from "../services/UtilsService";

// ============================================================================
// TYPES
// ============================================================================

export interface PaymentState {
  showPaymentStatus: boolean;
  paymentProcessing: boolean;
  transactionStatus: "pending" | "success" | "failure" | "processing";
  amountPaid: number;
  paytmTransaction: boolean;
  transactionId: string;
}

export interface LoanState {
  // Loans data
  ongoingLoans: Loan[];
  pastLoans: Loan[];
  selectedLoan: Loan | null;
  loansLoading: boolean;
  loansError: string | null;

  // Calculated values for selected loan
  fees: Fee;
  nextEmiDue: EmiDue;
  otherEmiDue: EmiDue;
  amountToPay: number;
  showQuickPay: 0 | 1 | 2 | 3;
  precloseDetails: PrecloseLoanDetails | null;

  // NACH data
  nachRepayment: NachRepayment | null;
  nachRepaymentArray: NachRepayment[];
  nachRepaymentError: boolean;

  // Payment state
  payment: PaymentState;

  // UI state
  viewMode: "ongoing" | "past";
  payAll: boolean;
}

export interface LoanContextType {
  state: LoanState;

  // Loan actions
  setOngoingLoans: (loans: Loan[]) => void;
  setPastLoans: (loans: Loan[]) => void;
  selectLoan: (loan: Loan | null) => void;
  setLoansLoading: (loading: boolean) => void;
  setLoansError: (error: string | null) => void;
  refreshLoans: () => void;

  // NACH actions
  setNachRepayment: (nach: NachRepayment | null) => void;
  setNachRepaymentArray: (nachArray: NachRepayment[]) => void;
  setNachRepaymentError: (error: boolean) => void;

  // Preclose actions
  setPrecloseDetails: (details: PrecloseLoanDetails | null) => void;

  // Payment actions
  setShowPaymentStatus: (show: boolean) => void;
  setPaymentProcessing: (processing: boolean) => void;
  setTransactionStatus: (
    status: "pending" | "success" | "failure" | "processing",
  ) => void;
  setAmountPaid: (amount: number) => void;
  setPaytmTransaction: (isPaytm: boolean) => void;
  setTransactionId: (id: string) => void;
  resetPaymentState: () => void;

  // UI actions
  setViewMode: (mode: "ongoing" | "past") => void;
  setPayAll: (payAll: boolean) => void;

  // Refresh callbacks
  onLoanRefresh: (callback: () => void) => () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialPaymentState: PaymentState = {
  showPaymentStatus: false,
  paymentProcessing: false,
  transactionStatus: "pending",
  amountPaid: 0,
  paytmTransaction: false,
  transactionId: "",
};

const initialState: LoanState = {
  ongoingLoans: [],
  pastLoans: [],
  selectedLoan: null,
  loansLoading: false,
  loansError: null,
  fees: createDefaultFee(),
  nextEmiDue: createDefaultEmiDue(),
  otherEmiDue: createDefaultEmiDue(),
  amountToPay: 0,
  showQuickPay: 0,
  precloseDetails: null,
  nachRepayment: null,
  nachRepaymentArray: [],
  nachRepaymentError: false,
  payment: initialPaymentState,
  viewMode: "ongoing",
  payAll: false,
};

// ============================================================================
// CONTEXT
// ============================================================================

const LoanContext = createContext<LoanContextType | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface LoanProviderProps {
  children: ReactNode;
}

export function LoanProvider({
  children,
}: LoanProviderProps): React.JSX.Element {
  const [state, setState] = useState<LoanState>(initialState);
  const refreshCallbacksRef = useRef<Set<() => void>>(new Set());

  // ============================================================================
  // CALCULATED VALUES (recalculated when loan changes)
  // ============================================================================

  // Calculate fees, EMI dues, etc. when selected loan changes
  useEffect(() => {
    if (!state.selectedLoan) {
      setState((prev) => ({
        ...prev,
        fees: createDefaultFee(),
        nextEmiDue: createDefaultEmiDue(),
        otherEmiDue: createDefaultEmiDue(),
        amountToPay: 0,
        showQuickPay: 0,
      }));
      return;
    }

    // Calculate fees
    const { fees } = utilsService.calculateFees(state.selectedLoan);

    // Calculate next EMI due
    const { nextEmiDue, firstUnpaidIndex } = utilsService.calculateNextEmiDue(
      state.selectedLoan,
    );

    // Calculate other EMI due
    const { otherEmiDue } = utilsService.calculateOtherEmiDue(
      state.selectedLoan,
      firstUnpaidIndex,
    );

    // Calculate amount to pay
    const { amountToPay } = utilsService.calculateAmountToPay(
      state.selectedLoan,
      state.payAll,
    );

    // Calculate show quick pay
    const showQuickPay = utilsService.calculateShowQuickPay(
      state.selectedLoan,
      state.nachRepayment,
      state.nachRepaymentArray,
      state.nachRepaymentError,
    );

    setState((prev) => ({
      ...prev,
      fees,
      nextEmiDue,
      otherEmiDue,
      amountToPay,
      showQuickPay,
    }));
  }, [
    state.selectedLoan,
    state.payAll,
    state.nachRepayment,
    state.nachRepaymentArray,
    state.nachRepaymentError,
  ]);

  // ============================================================================
  // LOAN ACTIONS
  // ============================================================================

  const setOngoingLoans = useCallback((loans: Loan[]) => {
    setState((prev) => ({ ...prev, ongoingLoans: loans }));
  }, []);

  const setPastLoans = useCallback((loans: Loan[]) => {
    setState((prev) => ({ ...prev, pastLoans: loans }));
  }, []);

  const selectLoan = useCallback((loan: Loan | null) => {
    setState((prev) => ({
      ...prev,
      selectedLoan: loan,
      // Reset NACH data when loan changes
      nachRepayment: null,
      nachRepaymentArray: [],
      nachRepaymentError: false,
      precloseDetails: null,
    }));
  }, []);

  const setLoansLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, loansLoading: loading }));
  }, []);

  const setLoansError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, loansError: error }));
  }, []);

  const refreshLoans = useCallback(() => {
    refreshCallbacksRef.current.forEach((callback) => callback());
  }, []);

  // ============================================================================
  // NACH ACTIONS
  // ============================================================================

  const setNachRepayment = useCallback((nach: NachRepayment | null) => {
    setState((prev) => ({ ...prev, nachRepayment: nach }));
  }, []);

  const setNachRepaymentArray = useCallback((nachArray: NachRepayment[]) => {
    setState((prev) => ({ ...prev, nachRepaymentArray: nachArray }));
  }, []);

  const setNachRepaymentError = useCallback((error: boolean) => {
    setState((prev) => ({ ...prev, nachRepaymentError: error }));
  }, []);

  // ============================================================================
  // PRECLOSE ACTIONS
  // ============================================================================

  const setPrecloseDetails = useCallback(
    (details: PrecloseLoanDetails | null) => {
      setState((prev) => ({ ...prev, precloseDetails: details }));
    },
    [],
  );

  // ============================================================================
  // PAYMENT ACTIONS
  // ============================================================================

  const setShowPaymentStatus = useCallback((show: boolean) => {
    setState((prev) => ({
      ...prev,
      payment: { ...prev.payment, showPaymentStatus: show },
    }));
  }, []);

  const setPaymentProcessing = useCallback((processing: boolean) => {
    setState((prev) => ({
      ...prev,
      payment: { ...prev.payment, paymentProcessing: processing },
    }));
  }, []);

  const setTransactionStatus = useCallback(
    (status: "pending" | "success" | "failure" | "processing") => {
      setState((prev) => ({
        ...prev,
        payment: { ...prev.payment, transactionStatus: status },
      }));
    },
    [],
  );

  const setAmountPaid = useCallback((amount: number) => {
    setState((prev) => ({
      ...prev,
      payment: { ...prev.payment, amountPaid: amount },
    }));
  }, []);

  const setPaytmTransaction = useCallback((isPaytm: boolean) => {
    setState((prev) => ({
      ...prev,
      payment: { ...prev.payment, paytmTransaction: isPaytm },
    }));
  }, []);

  const setTransactionId = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      payment: { ...prev.payment, transactionId: id },
    }));
  }, []);

  const resetPaymentState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      payment: initialPaymentState,
    }));
  }, []);

  // ============================================================================
  // UI ACTIONS
  // ============================================================================

  const setViewMode = useCallback((mode: "ongoing" | "past") => {
    setState((prev) => ({ ...prev, viewMode: mode }));
  }, []);

  const setPayAll = useCallback((payAll: boolean) => {
    setState((prev) => ({ ...prev, payAll }));
  }, []);

  // ============================================================================
  // REFRESH CALLBACKS
  // ============================================================================

  const onLoanRefresh = useCallback((callback: () => void): (() => void) => {
    refreshCallbacksRef.current.add(callback);
    return () => {
      refreshCallbacksRef.current.delete(callback);
    };
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value = useMemo<LoanContextType>(
    () => ({
      state,
      setOngoingLoans,
      setPastLoans,
      selectLoan,
      setLoansLoading,
      setLoansError,
      refreshLoans,
      setNachRepayment,
      setNachRepaymentArray,
      setNachRepaymentError,
      setPrecloseDetails,
      setShowPaymentStatus,
      setPaymentProcessing,
      setTransactionStatus,
      setAmountPaid,
      setPaytmTransaction,
      setTransactionId,
      resetPaymentState,
      setViewMode,
      setPayAll,
      onLoanRefresh,
    }),
    [
      state,
      setOngoingLoans,
      setPastLoans,
      selectLoan,
      setLoansLoading,
      setLoansError,
      refreshLoans,
      setNachRepayment,
      setNachRepaymentArray,
      setNachRepaymentError,
      setPrecloseDetails,
      setShowPaymentStatus,
      setPaymentProcessing,
      setTransactionStatus,
      setAmountPaid,
      setPaytmTransaction,
      setTransactionId,
      resetPaymentState,
      setViewMode,
      setPayAll,
      onLoanRefresh,
    ],
  );

  return <LoanContext.Provider value={value}>{children}</LoanContext.Provider>;
}

// ============================================================================
// HOOKS
// ============================================================================

export function useLoanContext(): LoanContextType {
  const context = useContext(LoanContext);
  if (!context) {
    throw new Error("useLoanContext must be used within a LoanProvider");
  }
  return context;
}

/**
 * Hook for accessing just the loan list state
 */
export function useLoans() {
  const {
    state,
    setOngoingLoans,
    setPastLoans,
    setLoansLoading,
    setLoansError,
    refreshLoans,
    onLoanRefresh,
  } = useLoanContext();

  return {
    ongoingLoans: state.ongoingLoans,
    pastLoans: state.pastLoans,
    isLoading: state.loansLoading,
    error: state.loansError,
    viewMode: state.viewMode,
    setOngoingLoans,
    setPastLoans,
    setLoading: setLoansLoading,
    setError: setLoansError,
    refresh: refreshLoans,
    onRefresh: onLoanRefresh,
  };
}

/**
 * Hook for accessing selected loan state
 */
export function useSelectedLoan() {
  const { state, selectLoan, setPayAll, setPrecloseDetails } = useLoanContext();

  return {
    loan: state.selectedLoan,
    fees: state.fees,
    nextEmiDue: state.nextEmiDue,
    otherEmiDue: state.otherEmiDue,
    amountToPay: state.amountToPay,
    showQuickPay: state.showQuickPay,
    payAll: state.payAll,
    precloseDetails: state.precloseDetails,
    select: selectLoan,
    setPayAll,
    setPrecloseDetails,
  };
}

/**
 * Hook for accessing payment state
 */
export function usePaymentState() {
  const {
    state,
    setShowPaymentStatus,
    setPaymentProcessing,
    setTransactionStatus,
    setAmountPaid,
    setPaytmTransaction,
    setTransactionId,
    resetPaymentState,
  } = useLoanContext();

  return {
    ...state.payment,
    setShowPaymentStatus,
    setPaymentProcessing,
    setTransactionStatus,
    setAmountPaid,
    setPaytmTransaction,
    setTransactionId,
    reset: resetPaymentState,
  };
}

/**
 * Hook for accessing NACH state
 */
export function useNachState() {
  const {
    state,
    setNachRepayment,
    setNachRepaymentArray,
    setNachRepaymentError,
  } = useLoanContext();

  return {
    nachRepayment: state.nachRepayment,
    nachRepaymentArray: state.nachRepaymentArray,
    nachRepaymentError: state.nachRepaymentError,
    setNachRepayment,
    setNachRepaymentArray,
    setNachRepaymentError,
  };
}
